import { ISelectItem } from "@components/inputs/select/Select.types";
import { SwitchType } from "@components/inputs/switch/Switch";
import { IGetValueArgs } from "@components/smart/FieldInfo";
import {
    AccountDefFormatter,
    getCommonFilterDefs,
    getCommonTableColumnsDefs
} from "@components/smart/GeneralFieldDefinition";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import {
    AccountAssignmentEntity,
    EntitySetName,
    EntityTypeName,
    IAccountAssignmentDocumentTypeEntity,
    ICompanyEntity,
    ItemTemplateEntity
} from "@odata/GeneratedEntityTypes";
import { BookkeepingCode, DocumentTypeCode } from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import {
    ALL_ACCOUNTS_REST_API,
    getAccountComparisonFunction,
    getAccountFormatter
} from "@pages/accountAssignment/AccountAssignment.utils.shared";
import i18next from "i18next";

import { BasicInputSizes, CacheStrategy, FieldType, LabelStatus, Sort, ValueType } from "../../enums";
import { TValue } from "../../global.types";
import { Model } from "../../model/Model";
import BindingContext from "../../odata/BindingContext";
import { IFormDef } from "../../views/formView/Form";
import { ISplitPageTableDef } from "../../views/table/TableView.utils";
import { setDefByEntityType } from "../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../PageUtils";
import { getDocumentTypeLabel } from "./AccountAssignment.utils";
import AccountAssignmentFormView from "./AccountAssignmentFormView";

// field with all accounts across all companies, distinct by Number and Name
export const allAccountsDef = {
    isRequired: true,
    type: FieldType.ComboBox,
    cacheStrategy: CacheStrategy.View,
    fieldSettings: {
        endpoint: ALL_ACCOUNTS_REST_API,
        preloadItems: true,
        transformFetchedItems: (items: ISelectItem[]): ISelectItem[] => {
            // cannot use formatter, as formatter has to take value directly from entity, because we are not able to pair items to values from entity
            return items.map((item: ISelectItem) => ({
                ...item,
                label: AccountDefFormatter(null, { item: item.additionalData }),
            }));
        }
    },
    width: BasicInputSizes.XL
};

export const AccAssCreditAccountPath = BindingContext.localContext("CreditAccount");
export const AccAssDebitAccountPath = BindingContext.localContext("DebitAccount");

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        isValueHelp: true,
        defaultFilters: [
            "ShortName",
            "Name",
            "DebitAccountNumber",
            "CreditAccountNumber",
            BindingContext.localContext("DocumentTypes")
        ],
        filterDefinition: {
            ShortName: {},
            Name: {},
            IsAllowedForAllCompanies: {
                label: i18next.t("AccountAssignment:Form.ForAllCompanies")
            },
            DebitAccountNumber: {},
            CreditAccountNumber: {},
            // once again, similar problem as we have e.g. in document form with account assignments
            // the source of the filter is different than what the AccountAssignment/DocumentItems points to (type AccountAssignmentDocumentType)
            // but we need to load the data from the DocumentTypes entity set (type DocumentType)
            // this causes mismatch between properties a fails on multiple places => using local binding context instead
            // todo enable this without local binding context
            [BindingContext.localContext("DocumentTypes")]: {
                label: i18next.t("AccountAssignment:AvailableFor"),
                filterName: ["/DocumentTypes/DocumentTypeCode"],
                isValueHelp: false,
                type: FieldType.MultiSelect,
                valueType: ValueType.String,
                fieldSettings: {
                    entitySet: EntitySetName.DocumentTypes,
                    displayName: "Name",
                    transformFetchedItems: (items) => {
                        return items.map((item: ISelectItem) => ({
                            id: item.id,
                            label: getDocumentTypeLabel({
                                Code: item.id as DocumentTypeCode,
                                Name: item.label
                            })
                        }));
                    }
                },
                defaultValue: []
            },
            Note: {},
            ...getCommonFilterDefs()
        }
    }];

    const table: ISplitPageTableDef = {
        filterBarDef,
        id: `${EntityTypeName.AccountAssignment}Table`,
        columns: [
            "ShortName",
            "Name",
            "DebitAccountNumber",
            "CreditAccountNumber"
        ],
        columnDefinition: {
            ShortName: { id: "ShortName" },
            Name: { id: "Name" },
            DebitAccountNumber: {
                additionalProperties: [{ id: "/DebitAccountName" }],
                formatter: getAccountFormatter("DebitAccount"),
                label: i18next.t("AccountAssignment:DebitAccount")
            },
            CreditAccountNumber: {
                additionalProperties: [{ id: "/CreditAccountName" }],
                formatter: getAccountFormatter("CreditAccount"),
                label: i18next.t("AccountAssignment:CreditAccount")
            },
            Note: {},
            IsAllowedForAllCompanies: {
                label: i18next.t("AccountAssignment:Form.ForAllCompanies"),
            },
            ...getCommonTableColumnsDefs(),
            DocumentTypes: {
                additionalProperties: [
                    { id: "DocumentType/Name" }
                ],
                formatter: (val: TValue, args?: IFormatOptions) => {
                    return args.entity.DocumentTypes
                            ?.map((documentType: IAccountAssignmentDocumentTypeEntity) => documentType.DocumentType.Name).join(", ");
                }
            }

        },
        title: i18next.t("AccountAssignment:Title"),
        initialSortBy: [{
            id: "ShortName", sort: Sort.Asc
        }]
    };

    const summary: ISummaryItem[] = [];

    const form: IFormDef = {
        id: `${EntityTypeName.AccountAssignment}Form`,
        summary,
        formControl: AccountAssignmentFormView,
        translationFiles: getDefinitions.translationFiles,
        isDeletable: true,
        getItemBreadCrumbText: (storage: Model) =>
                getItemBreadCrumbsText(storage, i18next.t("AccountAssignment:NewAccountAssignment"), storage.data.entity?.Name),
        additionalProperties: [
            { id: AccountAssignmentEntity.DebitAccountNumber }, { id: AccountAssignmentEntity.DebitAccountName },
            { id: AccountAssignmentEntity.CreditAccountNumber }, { id: AccountAssignmentEntity.CreditAccountName }
        ],
        fieldDefinition: {
            Name: {
                width: BasicInputSizes.XL
            },
            ShortName: {
                width: BasicInputSizes.S
            },
            [AccAssCreditAccountPath]: {
                label: i18next.t("AccountAssignment:CreditAccount"),
                ...allAccountsDef,
                comparisonFunction: getAccountComparisonFunction("CreditAccount"),
                formatter: getAccountFormatter("CreditAccount")
            },
            [AccAssDebitAccountPath]: {
                label: i18next.t("AccountAssignment:DebitAccount"),
                ...allAccountsDef,
                comparisonFunction: getAccountComparisonFunction("DebitAccount"),
                formatter: getAccountFormatter("DebitAccount")
            },
            Note: {
                type: FieldType.EditableText,
                labelStatus: LabelStatus.Removed
            },
            DocumentTypes: {
                fieldSettings: {
                    keyPath: "DocumentType/Code"
                },
                customizationData: {
                    isRequired: true
                },
                additionalProperties: [{
                    id: "DocumentType"
                }, {
                    id: "DocumentType/Name",
                    isRequired: false
                }],
                labelStatus: LabelStatus.Removed,
                type: FieldType.CheckboxGroup
            },
            IsAllowedForAllCompanies: {
                type: FieldType.Switch,
                fieldSettings: {
                    type: SwitchType.YesNo,
                },
                label: i18next.t("AccountAssignment:Form.ForAllCompanies"),
                isRequired: true,
                defaultValue: true,
                affectedFields: [{ id: ItemTemplateEntity.AllowedCompanies }],
                customizationData: {
                    dependents: [ItemTemplateEntity.AllowedCompanies]
                }
            },
            AllowedCompanies: {
                type: FieldType.MultiSelect,
                label: i18next.t("AccountAssignment:Form.UseForCompanies"),
                isRequired: true,
                isCollectionField: true,
                width: BasicInputSizes.XL,
                fieldSettings: {
                    keyPath: "Id",
                    items: [],
                    itemsForRender: (origItems, args: IGetValueArgs) => {
                        const companies = args.storage.context.getData().companies;
                        return companies?.filter((company: ICompanyEntity) => {
                            return company.Accounting?.BookkeepingCode === BookkeepingCode.DoubleentryBookkeeping;
                        }).map(({ Id, Name }: ICompanyEntity) => ({
                            id: Id,
                            label: Name
                        }));
                    }
                },
                isVisible: (args: IGetValueArgs) => {
                    return !args.storage.getValueByPath(AccountAssignmentEntity.IsAllowedForAllCompanies);
                }
            }
        },
        groups: [
            {
                id: "basic",
                rows: [[{ id: AccountAssignmentEntity.ShortName }, { id: AccountAssignmentEntity.Name },
                    { id: AccAssDebitAccountPath },
                    { id: AccAssCreditAccountPath }
                ]]
            },
            {
                id: "note",
                title: i18next.t("Common:General.Note"),
                rows: [[{ id: AccountAssignmentEntity.Note }]]
            }, {
                id: "companies",
                title: i18next.t("AccountAssignment:Form.Companies"),
                rows: [[{ id: AccountAssignmentEntity.IsAllowedForAllCompanies }, { id: AccountAssignmentEntity.AllowedCompanies }]]
            }, {
                id: "availableFor",
                title: i18next.t("Common:Form.AvailableFor"),
                rows: [[{ id: AccountAssignmentEntity.DocumentTypes }]]
            }]
    };

    return {
        entitySet: EntitySetName.AccountAssignments,
        table,
        form
    };
};

getDefinitions.translationFiles = ["AccountAssignment"];
setDefByEntityType(EntityTypeName.AccountAssignment, getDefinitions);